import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { kr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Panel } from '../common/Panel';
import { formatWholeKr, tooltipContentStyle, tooltipItemStyle } from './chart';

type Breakdown = RouterOutputs['insights']['spendingBreakdown'];

/** The four largest groups get their own color; the rest share the neutral "Övrigt" slice */
const SLICE_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];
const REST_COLOR = 'var(--chart-5)';

/** Share of the period's spending per category group, as a donut with a legend list. */
export function CategoryShareCard({ breakdown }: { breakdown: Breakdown | undefined }) {
  const groups = [
    ...(breakdown?.groups ?? []).map((g) => ({ name: g.name, value: g.total })),
    ...(breakdown && breakdown.uncategorized.total > 0
      ? [{ name: 'Utan kategori', value: breakdown.uncategorized.total }]
      : []),
  ].sort((a, b) => b.value - a.value);

  const head = groups.slice(0, SLICE_COLORS.length);
  const restTotal = groups.slice(SLICE_COLORS.length).reduce((s, g) => s + g.value, 0);
  const slices = [
    ...head.map((g, i) => ({ ...g, color: SLICE_COLORS[i] ?? REST_COLOR })),
    ...(restTotal > 0 ? [{ name: 'Övrigt', value: restTotal, color: REST_COLOR }] : []),
  ];
  const total = slices.reduce((s, g) => s + g.value, 0);

  return (
    <Panel title="Fördelning" description="Alla utgifter per kategorigrupp">
      {total > 0 ? (
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="relative size-44 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices.map((s) => ({ name: s.name, value: Math.round(s.value / 100) }))}
                  dataKey="value"
                  innerRadius="66%"
                  outerRadius="100%"
                  paddingAngle={2}
                  stroke="var(--card)"
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  {slices.map((s) => (
                    <Cell key={s.name} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [formatWholeKr(value), name]}
                  contentStyle={tooltipContentStyle}
                  itemStyle={tooltipItemStyle}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[11px] text-muted-foreground">Totalt</span>
              <span className="text-sm font-semibold tabular-nums">{kr(total)}</span>
            </div>
          </div>
          <ul className="w-full min-w-0 space-y-2 sm:flex-1">
            {slices.map((s) => (
              <li key={s.name} className="flex items-center gap-2 text-sm">
                <span
                  aria-hidden
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                <span className="shrink-0 text-muted-foreground tabular-nums">{kr(s.value)}</span>
                <span className="w-10 shrink-0 text-right font-medium tabular-nums">
                  {Math.round((s.value / total) * 100)} %
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Inga utgifter under perioden än.
        </p>
      )}
    </Panel>
  );
}
